import { default as initializeRenderCanvas } from "../canvas/initializeRenderCanvas.js";
import { default as generateColorLut } from "../lookupTable/generateColorLut.js";

import { default as saveLastRendered } from "./saveLastRendered.js";
import { default as doesImageNeedToBeRendered } from "./doesImageNeedToBeRendered.js";
import { default as storedColorPixelDataToCanvasImageData } from "../canvas/storedColorPixelDataToCanvasImageData.js";
import { default as storedRGBAPixelDataToCanvasImageData } from "../canvas/storedRGBAPixelDataToCanvasImageData.js";

/**
 * Calculate the transform for a  enabled element
 *
 * @param {EnabledElement} enabledElement The  Enabled Element
 * @param {Number} [scale] The viewport scale
 * @return {Transform} The current transform
 * @memberof Internal
 */
function calculateTransform(enabledElement, scale) {
  // For now we will calculate it every time it is requested.
  // In the future, we may want to cache it in the enabled element to speed things up.

  const transform = new Transform();

  // Move to center of canvas
  transform.translate(
    enabledElement.canvas.width / 2,
    enabledElement.canvas.height / 2
  );

  // Apply the rotation before scaling for non square pixels
  const angle = enabledElement.viewport.rotation;

  if (angle !== 0) {
    transform.rotate((angle * Math.PI) / 180);
  }

  // Apply the scale
  let widthScale = enabledElement.viewport.scale;
  let heightScale = enabledElement.viewport.scale;

  const width =
    enabledElement.viewport.displayedArea.brhc.x -
    (enabledElement.viewport.displayedArea.tlhc.x - 1);
  const height =
    enabledElement.viewport.displayedArea.brhc.y -
    (enabledElement.viewport.displayedArea.tlhc.y - 1);

  if (enabledElement.viewport.displayedArea.presentationSizeMode === "NONE") {
    if (
      enabledElement.image.rowPixelSpacing <
      enabledElement.image.columnPixelSpacing
    ) {
      widthScale *=
        enabledElement.image.columnPixelSpacing /
        enabledElement.image.rowPixelSpacing;
    } else if (
      enabledElement.image.columnPixelSpacing <
      enabledElement.image.rowPixelSpacing
    ) {
      heightScale *=
        enabledElement.image.rowPixelSpacing /
        enabledElement.image.columnPixelSpacing;
    }
  } else {
    // These should be good for "TRUE SIZE" and "MAGNIFY"
    widthScale = enabledElement.viewport.displayedArea.columnPixelSpacing;
    heightScale = enabledElement.viewport.displayedArea.rowPixelSpacing;

    if (
      enabledElement.viewport.displayedArea.presentationSizeMode ===
      "SCALE TO FIT"
    ) {
      // Fit TRUE IMAGE image (width/height) to window
      const verticalScale =
        enabledElement.canvas.height / (height * heightScale);
      const horizontalScale =
        enabledElement.canvas.width / (width * widthScale);

      // Apply new scale
      widthScale = heightScale = Math.min(horizontalScale, verticalScale);

      if (
        enabledElement.viewport.displayedArea.rowPixelSpacing <
        enabledElement.viewport.displayedArea.columnPixelSpacing
      ) {
        widthScale *=
          enabledElement.viewport.displayedArea.columnPixelSpacing /
          enabledElement.viewport.displayedArea.rowPixelSpacing;
      } else if (
        enabledElement.viewport.displayedArea.columnPixelSpacing <
        enabledElement.viewport.displayedArea.rowPixelSpacing
      ) {
        heightScale *=
          enabledElement.viewport.displayedArea.rowPixelSpacing /
          enabledElement.viewport.displayedArea.columnPixelSpacing;
      }
    }
  }

  transform.scale(widthScale, heightScale);

  // Unrotate to so we can translate unrotated
  if (angle !== 0) {
    transform.rotate((-angle * Math.PI) / 180);
  }

  // Apply the pan offset
  transform.translate(
    enabledElement.viewport.translation.x,
    enabledElement.viewport.translation.y
  );

  // Rotate again so we can apply general scale
  if (angle !== 0) {
    transform.rotate((angle * Math.PI) / 180);
  }

  if (scale !== undefined) {
    // Apply the font scale
    transform.scale(scale, scale);
  }

  // Apply Flip if required
  if (enabledElement.viewport.hflip) {
    transform.scale(-1, 1);
  }

  if (enabledElement.viewport.vflip) {
    transform.scale(1, -1);
  }

  // Move back from center of image
  transform.translate(-width / 2, -height / 2);

  return transform;
}

/**
 * Sets the canvas context transformation matrix to the pixel coordinate system.  This allows
 * geometry to be driven using the canvas context using coordinates in the pixel coordinate system
 * @param {EnabledElement} enabledElement The
 * @param {CanvasRenderingContext2D} context The CanvasRenderingContext2D for the enabledElement's Canvas
 * @param {Number} [scale] Optional scale to apply
 * @returns {void}
 */
function setToPixelCoordinateSystem(enabledElement, context, scale) {
  if (enabledElement === undefined) {
    throw new Error(
      "setToPixelCoordinateSystem: parameter enabledElement must not be undefined"
    );
  }
  if (context === undefined) {
    throw new Error(
      "setToPixelCoordinateSystem: parameter context must not be undefined"
    );
  }

  const transform = calculateTransform(enabledElement, scale);

  context.setTransform(
    transform.m[0],
    transform.m[1],
    transform.m[2],
    transform.m[3],
    transform.m[4],
    transform.m[5]
  );
}

/**
 * Generates an appropriate Look Up Table to render the given image with the given window width and level (specified in the viewport)
 * Uses an internal cache for performance
 *
 * @param {Object} image  The image to be rendered
 * @param {Object} viewport The viewport values used for rendering
 * @returns {Uint8ClampedArray} Look Up Table array.
 * @memberof rendering
 */
function getLut(image, viewport) {
  // If we have a cached lut and it has the right values, return it immediately
  if (
    image.cachedLut !== undefined &&
    image.cachedLut.windowCenter === viewport.voi.windowCenter &&
    image.cachedLut.windowWidth === viewport.voi.windowWidth &&
    image.cachedLut.invert === viewport.invert
  ) {
    return image.cachedLut.lutArray;
  }

  // Lut is invalid or not present, regenerate it and cache it
  generateColorLut(
    image,
    viewport.voi.windowWidth,
    viewport.voi.windowCenter,
    viewport.invert
  );
  image.cachedLut.windowWidth = viewport.voi.windowWidth;
  image.cachedLut.windowCenter = viewport.voi.windowCenter;
  image.cachedLut.invert = viewport.invert;

  return image.cachedLut.lutArray;
}

/**
 * Returns an appropriate canvas to render the Image. If the canvas available in the cache is appropriate
 * it is returned, otherwise adjustments are made. It also sets the color transfer functions.
 *
 * @param {Object} enabledElement The  enabled element
 * @param {Object} image The image to be rendered
 * @param {Boolean} invalidated Is pixel data valid
 * @returns {HTMLCanvasElement} An appropriate canvas for rendering the image
 * @memberof rendering
 */
function getRenderCanvas(enabledElement, image, invalidated) {
  const canvasWasColor =
    enabledElement.renderingTools.lastRenderedIsColor === true;

  if (!enabledElement.renderingTools.renderCanvas || !canvasWasColor) {
    enabledElement.renderingTools.renderCanvas = document.createElement(
      "canvas"
    );
  }

  const renderCanvas = enabledElement.renderingTools.renderCanvas;

  // The ww/wc is identity and not inverted - get a canvas with the image rendered into it for
  // Fast drawing
  if (
    enabledElement.viewport.voi.windowWidth === 255 &&
    enabledElement.viewport.voi.windowCenter === 128 &&
    enabledElement.viewport.invert === false &&
    image.getCanvas &&
    image.getCanvas()
  ) {
    return image.getCanvas();
  }

  // Apply the lut to the stored pixel data onto the render canvas
  if (
    doesImageNeedToBeRendered(enabledElement, image) === false &&
    invalidated !== true
  ) {
    return renderCanvas;
  }

  // If our render canvas does not match the size of this image reset it
  // NOTE: This might be inefficient if we are updating multiple images of different
  // Sizes frequently.
  if (
    renderCanvas.width !== image.width ||
    renderCanvas.height !== image.height
  ) {
    initializeRenderCanvas(enabledElement, image);
  }

  // Get the lut to use
  const colorLut = getLut(image, enabledElement.viewport);

  image.stats = image.stats || {};

  const renderCanvasData = enabledElement.renderingTools.renderCanvasData;
  const renderCanvasContext = enabledElement.renderingTools.renderCanvasContext;

  // The color image voi/invert has been modified - apply the lut to the underlying
  // Pixel data and put it into the renderCanvas
  image.rgba = false; // for temp
  if (image.rgba) {
    storedRGBAPixelDataToCanvasImageData(
      image,
      colorLut,
      renderCanvasData.data
    );
  } else {
    storedColorPixelDataToCanvasImageData(
      image,
      colorLut,
      renderCanvasData.data
    );
  }

  renderCanvasContext.putImageData(renderCanvasData, 0, 0);

  return renderCanvas;
}

/**
 * API function to render a color image to an enabled element
 *
 * @param {EnabledElement} enabledElement The  Enabled Element to redraw
 * @param {Boolean} invalidated - true if pixel data has been invalidated and cached rendering should not be used
 * @returns {void}
 * @memberof rendering
 */
export default function renderColorImage(enabledElement, invalidated) {
  if (enabledElement === undefined) {
    throw new Error(
      "renderColorImage: enabledElement parameter must not be undefined"
    );
  }

  const image = enabledElement.image;

  if (image === undefined) {
    throw new Error(
      "renderColorImage: image must be loaded before it can be drawn"
    );
  }

  // Get the canvas context and reset the transform
  const context = enabledElement.canvas.getContext("2d");

  context.setTransform(1, 0, 0, 1, 0, 0);

  // Clear the canvas
  context.fillStyle = "black";
  context.fillRect(
    0,
    0,
    enabledElement.canvas.width,
    enabledElement.canvas.height
  );

  // Turn off image smooth/interpolation if pixelReplication is set in the viewport
  context.imageSmoothingEnabled = !enabledElement.viewport.pixelReplication;
  context.mozImageSmoothingEnabled = context.imageSmoothingEnabled;

  // Save the canvas context state and apply the viewport properties
  setToPixelCoordinateSystem(enabledElement, context);

  let renderCanvas;

  // If no options are set we will retrieve the renderCanvas through the
  // Normal Canvas rendering path
  renderCanvas = getRenderCanvas(enabledElement, image, invalidated);

  const sx = enabledElement.viewport.displayedArea.tlhc.x - 1;
  const sy = enabledElement.viewport.displayedArea.tlhc.y - 1;
  const width = enabledElement.viewport.displayedArea.brhc.x - sx;
  const height = enabledElement.viewport.displayedArea.brhc.y - sy;

  context.drawImage(renderCanvas, sx, sy, width, height, 0, 0, width, height);

  enabledElement.renderingTools = saveLastRendered(enabledElement);
}

// Remember that this does not account for any CSS transforms applied to the canvas
class Transform {
  constructor() {
    this.reset();
  }

  reset() {
    this.m = [1, 0, 0, 1, 0, 0];
  }

  clone() {
    const transform = new Transform();

    transform.m[0] = this.m[0];
    transform.m[1] = this.m[1];
    transform.m[2] = this.m[2];
    transform.m[3] = this.m[3];
    transform.m[4] = this.m[4];
    transform.m[5] = this.m[5];

    return transform;
  }

  multiply(matrix) {
    const m11 = this.m[0] * matrix.m[0] + this.m[2] * matrix.m[1];
    const m12 = this.m[1] * matrix.m[0] + this.m[3] * matrix.m[1];

    const m21 = this.m[0] * matrix.m[2] + this.m[2] * matrix.m[3];
    const m22 = this.m[1] * matrix.m[2] + this.m[3] * matrix.m[3];

    const dx = this.m[0] * matrix.m[4] + this.m[2] * matrix.m[5] + this.m[4];
    const dy = this.m[1] * matrix.m[4] + this.m[3] * matrix.m[5] + this.m[5];

    this.m[0] = m11;
    this.m[1] = m12;
    this.m[2] = m21;
    this.m[3] = m22;
    this.m[4] = dx;
    this.m[5] = dy;
  }

  invert() {
    const d = 1 / (this.m[0] * this.m[3] - this.m[1] * this.m[2]);
    const m0 = this.m[3] * d;
    const m1 = -this.m[1] * d;
    const m2 = -this.m[2] * d;
    const m3 = this.m[0] * d;
    const m4 = d * (this.m[2] * this.m[5] - this.m[3] * this.m[4]);
    const m5 = d * (this.m[1] * this.m[4] - this.m[0] * this.m[5]);

    this.m[0] = m0;
    this.m[1] = m1;
    this.m[2] = m2;
    this.m[3] = m3;
    this.m[4] = m4;
    this.m[5] = m5;
  }

  rotate(rad) {
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const m11 = this.m[0] * c + this.m[2] * s;
    const m12 = this.m[1] * c + this.m[3] * s;
    const m21 = this.m[0] * -s + this.m[2] * c;
    const m22 = this.m[1] * -s + this.m[3] * c;

    this.m[0] = m11;
    this.m[1] = m12;
    this.m[2] = m21;
    this.m[3] = m22;
  }

  translate(x, y) {
    this.m[4] += this.m[0] * x + this.m[2] * y;
    this.m[5] += this.m[1] * x + this.m[3] * y;
  }

  scale(sx, sy) {
    this.m[0] *= sx;
    this.m[1] *= sx;
    this.m[2] *= sy;
    this.m[3] *= sy;
  }

  transformPoint(px, py) {
    const x = px;
    const y = py;

    px = x * this.m[0] + y * this.m[2] + this.m[4];
    py = x * this.m[1] + y * this.m[3] + this.m[5];

    return {
      x: px,
      y: py
    };
  }
}
